import type { Edge, Node } from '@xyflow/react';

import type { Transport, TransportRequest } from '../../../services/controlTowerApi';
import { requestRow, type TransportRow, transportRows } from '../../transports/transportRows';

export type LiveGraphData = {
    kind: 'request' | 'transport';
    rows: TransportRow[];
    reference?: 'not_loaded' | 'missing';
};
export type LiveGraphNode = Node<LiveGraphData, 'liveGraph'>;
type LiveGraphEdgeData = { row: TransportRow; reference?: LiveGraphData['reference'] };
export type LiveGraphEdge = Edge<LiveGraphEdgeData> & { data: LiveGraphEdgeData };

export const CARD_WIDTH = 300;
export const GROUP_HEIGHT_ESTIMATE = 148;

// Only fields already rendered by the original view enter the graph's display model.
export const buildLiveGraph = (requests: TransportRequest[], transports: Transport[]) => {
    const nodes = new Map<string, LiveGraphNode>();
    requests.forEach(request => {
        const id = `request:${request.id}`;
        nodes.set(id, {
            id,
            type: 'liveGraph',
            position: { x: 0, y: 0 },
            data: { kind: 'request', rows: [requestRow(request)] },
        });
    });
    const edges: LiveGraphEdge[] = [];
    // Resource IDs, rather than transport numbers, identify shared transport cards.
    for (const transport of new Map(transports.map(item => [item.id, item])).values()) {
        const rows = transportRows(transport);
        if (!rows.length) {
            continue;
        }
        const target = `transport:${transport.id}`;
        nodes.set(target, {
            id: target,
            type: 'liveGraph',
            position: { x: 0, y: 0 },
            data: { kind: 'transport', rows },
        });
        rows.forEach(row => {
            const source = `request:${row.requestId}`;
            const existing = nodes.get(source);
            if (!existing || existing.data.reference) {
                nodes.set(source, {
                    id: source,
                    type: 'liveGraph',
                    position: { x: 0, y: 0 },
                    data: {
                        kind: 'request',
                        rows: [],
                        reference:
                            row.requestResolved || existing?.data.reference === 'not_loaded' ? 'not_loaded' : 'missing',
                    },
                });
            }
            edges.push({
                id: row.id,
                source,
                target,
                targetHandle: row.id,
                data: { row },
            });
        });
    }
    return {
        nodes: [...nodes.values()],
        edges: edges.map(edge => ({
            ...edge,
            data: { ...edge.data, reference: nodes.get(edge.source)?.data.reference },
        })),
    };
};

export const cardHeight = (node: LiveGraphNode) => {
    if (node.measured?.height) {
        return node.measured.height;
    }
    if (node.data.reference) {
        return 102;
    }
    return node.data.kind === 'request' ? 238 : 104 + node.data.rows.length * GROUP_HEIGHT_ESTIMATE;
};

export const connectedSelection = (selected: string | null, edges: LiveGraphEdge[]) => {
    const nodes = new Set<string>();
    const connections = new Set<string>();
    if (selected) {
        if (!edges.some(edge => edge.id === selected)) {
            nodes.add(selected);
        }
        edges.forEach(edge => {
            if (edge.source === selected || edge.target === selected || edge.id === selected) {
                nodes.add(edge.source);
                nodes.add(edge.target);
                connections.add(edge.id);
            }
        });
    }
    return { nodes, edges: connections };
};
